import React, { useContext, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import InputQueryInterface from '@totem/components/ui_components/reportSettings/InputQueryInterface';
import {
  FilterField,
  FilterOption,
  Pagination,
  SortField,
} from '@totem/components/ui_components/reportSettings/types';
import { PersistentFilterFields } from '@totem/components/userContext/persistence/types';
import UserPersistenceContext from '@totem/components/userContext/persistence/UserPersistenceContext';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Filter, Paging, Params } from '@totem/types/common';
import { FilterValue } from '@totem/types/events';
import { isNotNull, isNull } from '@totem/utilities/common';
import logger from '@totem/utilities/logger';

const SAVE_PERSISTENCE_DEBOUNCE_TIME = 2000;
const EMPTY_OPTIONS: FilterOption[] = [];

type Props = {
  componentKey?: string;
  componentLabel?: string;
  children?: React.ReactNode;
  defaultPageSize?: number;
  filterConfig: FilterField[];
  defaultSort?: SortField;
  staticFilters?: Filter[];
  defaultFilters?: Filter[];
  useUrlParams?: boolean;
};

const InputContainer = ({
  children,
  componentKey,
  componentLabel = componentKey,
  filterConfig,
  defaultPageSize = 10,
  defaultSort,
  staticFilters,
  defaultFilters,
  useUrlParams,
}: Props) => {
  const [searchParams] = useSearchParams();
  const {
    persistenceLoaded,
    persistenceOrganizationId,
    getComponentPersistence,
    updateComponentPersistence,
  } = useContext(UserPersistenceContext);
  const { userProfile } = useContext(UserProfileContext);
  const organizationId = userProfile?.organization?.id ?? '';

  const buildInitialFilters = (): FilterField[] => {
    if (!isNotNull(defaultFilters) || defaultFilters.length === 0) {
      return filterConfig;
    }
    const hasUrlFilters =
      isNotNull(useUrlParams) &&
      useUrlParams &&
      filterConfig.some((flt) => searchParams.getAll(flt.key).length > 0);
    if (hasUrlFilters) return filterConfig;

    return filterConfig.map((flt) => {
      const defFilter = defaultFilters.find((def) => def.field === flt.key);
      if (!isNotNull(defFilter)) return flt;
      if (flt.multiple) {
        return {
          ...flt,
          values: defFilter.values.map((val) => ({
            value: val,
            text: val,
          })) as FilterValue[],
        };
      }
      return { ...flt, value: defFilter.values[0] ?? '' };
    });
  };

  const [pagination, setPagination] = React.useState<Pagination>({
    page: 1,
    pageSize: isNotNull(defaultPageSize) ? defaultPageSize : 10,
  });

  const [sort, setSort] = React.useState<SortField>({
    field: '',
    direction: 'ascend',
  });

  const [filters, setFilters] =
    React.useState<FilterField[]>(buildInitialFilters);

  const [fixedStaticFilters, setFixedStaticFilters] = React.useState<Filter[]>(
    [],
  );
  const [updateCount, setUpdateCount] = React.useState(0);
  const [filterUpdateCount, setFilterUpdateCount] = React.useState(0);
  const [pagingUpdateCount, setPagingUpdateCount] = React.useState(0);
  const [sortingUpdateCount, setSortingUpdateCount] = React.useState(0);
  const filterDebounceTimer = React.useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const persistenceDebounceTimer = React.useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const persistenceApplied = React.useRef(false);
  const appliedOrganizationId = React.useRef<string | undefined>(undefined);

  // isReady is true immediately when there is no componentKey (nothing to load),
  // and becomes true after persistence has been applied when componentKey is set.
  const [isReady, setIsReady] = React.useState(!isNotNull(componentKey));

  // Keep refs current so debounce callbacks read latest values
  const filtersRef = React.useRef(filters);
  const sortRef = React.useRef(sort);
  filtersRef.current = filters;

  sortRef.current = sort;

  useEffect(() => {
    return () => {
      if (filterDebounceTimer.current !== null) {
        clearTimeout(filterDebounceTimer.current);
      }
      if (persistenceDebounceTimer.current !== null) {
        clearTimeout(persistenceDebounceTimer.current);
      }
    };
  }, []);

  // Apply persisted defaults once persistence data loads, then signal ready.
  // Depends on persistenceLoaded (not persistenceData) so it fires exactly once
  // after the fetch settles — whether or not there is any saved data for this key.
  //
  // Also re-applies when the user's organization changes. Switching organizations
  // does not remount this component when the route stays the same (e.g. the org
  // switcher dropdown), so without this, filters/sort/paging picked while viewing
  // the previous organization (including values like a buildingId that don't
  // exist in the new one) would keep showing until manually cleared.
  //
  // persistenceOrganizationId must match the current organization before this
  // runs — this effect can fire (because organizationId itself just changed)
  // before UserPersistenceContainer's own effect has reset persistenceLoaded
  // for the reload, in which case persistenceLoaded would still read true
  // while persistenceData is still the previous organization's. Without this
  // check we'd apply that stale data and mark ourselves done for the new org,
  // then ignore the correct data once it actually arrives.
  useEffect(() => {
    if (
      !isNotNull(componentKey) ||
      !persistenceLoaded ||
      persistenceOrganizationId !== organizationId
    )
      return;

    const isOrganizationChange =
      appliedOrganizationId.current !== undefined &&
      appliedOrganizationId.current !== organizationId;

    if (persistenceApplied.current && !isOrganizationChange) return;

    persistenceApplied.current = true;

    appliedOrganizationId.current = organizationId;

    const baseline = buildInitialFilters();
    const saved = getComponentPersistence(componentKey);

    // Filter options are supplied by the owning container (setFilterOptions),
    // not by filterConfig, so resetting to the baseline would wipe them. The
    // container may already have loaded the new organization's options before
    // this runs, so carry them across rather than leaving the dropdowns empty.
    const baselineWithOptions = (prev: FilterField[]): FilterField[] =>
      baseline.map((flt) => {
        const existing = prev.find((chk) => chk.key === flt.key);
        return isNotNull(existing?.options)
          ? { ...flt, options: existing.options }
          : flt;
      });

    if (isOrganizationChange) {
      setFilters(baselineWithOptions);

      setSort({ field: '', direction: 'ascend' });

      setPagination({
        page: 1,
        pageSize: isNotNull(defaultPageSize) ? defaultPageSize : 10,
      });
    }

    if (isNotNull(saved)) {
      if (isNotNull(saved.paging)) {
        setPagination({
          page: 1,
          pageSize: saved.paging.pageSize > 0 ? saved.paging.pageSize : 10,
        });

        setPagingUpdateCount((prev) => prev + 1);
      }

      if (isNotNull(saved.sort) && saved.sort.field !== '') {
        setSort(saved.sort);

        setSortingUpdateCount((prev) => prev + 1);
      }

      setFilters((prev) =>
        (isOrganizationChange ? baselineWithOptions(prev) : prev).map((flt) => {
          if (flt.allowPersistence === false) return flt;
          const inDefaultFilters =
            isNotNull(defaultFilters) &&
            defaultFilters.some((def) => def.field === flt.key);
          if (inDefaultFilters) return flt;

          const savedFilter = saved.filters?.find((sf) => sf.key === flt.key);
          if (isNull(savedFilter)) return flt;

          if (flt.multiple) {
            return { ...flt, values: savedFilter.values ?? [] };
          }
          return { ...flt, value: savedFilter.value ?? '' };
        }),
      );

      setFilterUpdateCount((prev) => prev + 1);
    } else if (isOrganizationChange) {
      setFilterUpdateCount((prev) => prev + 1);
    }

    setIsReady(true);
  }, [
    componentKey,
    persistenceLoaded,
    organizationId,
    persistenceOrganizationId,
  ]);

  // Debounced save to persistence on filter, sort, or paging changes
  useEffect(() => {
    if (!isNotNull(componentKey)) return;
    if (
      sortingUpdateCount === 0 &&
      filterUpdateCount === 0 &&
      pagingUpdateCount === 0
    )
      return;

    if (persistenceDebounceTimer.current !== null) {
      clearTimeout(persistenceDebounceTimer.current);
    }

    persistenceDebounceTimer.current = setTimeout(() => {
      const persistentFilters: PersistentFilterFields = filtersRef.current
        .filter(
          (flt) =>
            flt.allowPersistence !== false &&
            ((flt.multiple && flt.values.length > 0) ||
              (!flt.multiple && flt.value !== '')),
        )
        .map((flt) => ({
          key: flt.key,
          value: flt.value ?? null,
          values: flt.values ?? [],
          operator: flt.operator ?? null,
        }));

      const paging: Paging = {
        page:
          isNotNull(pagination.page) && pagination.page > 0
            ? pagination.page
            : 1,
        pageSize:
          isNotNull(pagination.pageSize) && pagination.pageSize > 0
            ? pagination.pageSize
            : 10,
      };

      updateComponentPersistence(
        componentKey,
        componentLabel,
        paging,
        sortRef.current,
        persistentFilters,
      );

      persistenceDebounceTimer.current = null;
    }, SAVE_PERSISTENCE_DEBOUNCE_TIME);
  }, [
    componentKey,
    pagingUpdateCount,
    sortingUpdateCount,
    filterUpdateCount,
    componentLabel,
  ]);

  const setStaticFilters = (newStaticFilters: Filter[]) => {
    if (isNotNull(newStaticFilters)) {
      setFixedStaticFilters(newStaticFilters);
    } else {
      setFixedStaticFilters([]);
    }
  };

  const handlePagination = (page: number, pageSize: number): void => {
    setPagination((prev) => {
      return {
        page:
          isNotNull(pageSize) && pageSize !== prev.pageSize
            ? 1
            : isNotNull(page)
              ? page
              : prev.page,
        pageSize: isNotNull(pageSize) ? pageSize : prev.pageSize,
      };
    });

    setPagingUpdateCount((prev) => prev + 1);
  };

  const handleSort = (field: string, direction: string): void => {
    if (sort.field !== field || sort.direction !== direction) {
      setPagination((prev) => {
        return {
          page: 1,
          pageSize: prev.pageSize,
        };
      });
    }

    if (isNotNull(field) && field !== '') {
      let newDirection = direction;
      if (newDirection !== 'ascend' && newDirection !== 'descend') {
        newDirection = 'ascend';
      }
      setSort({
        field,
        direction: newDirection,
      });

      setSortingUpdateCount((prev) => prev + 1);
    }
  };

  const filterValueToString = (key: string, value: string): React.ReactNode => {
    const foundFilter = filters.find((chk) => chk.key === key);
    if (isNotNull(foundFilter) && isNotNull(foundFilter.options)) {
      const foundValue = foundFilter.options.find((chk) => chk.value === value);
      if (isNotNull(foundValue)) {
        return foundValue.text;
      }
    }

    return value;
  };

  const handleFilter = (
    filterKey: string,
    filterValues: string[] | string,
  ): void => {
    setFilters((prev) => {
      const isMultiple = Array.isArray(filterValues);
      let cleanFilterValues: string[] = [];
      if (isMultiple) {
        cleanFilterValues = filterValues.filter(
          (item) => isNotNull(item) && item !== '',
        );
      }
      const exists = prev.some((flt) => flt.key === filterKey);
      if (exists) {
        return prev.map((flt) => {
          if (flt.key !== filterKey || flt.filterField === '') return flt;
          return isMultiple
            ? {
                ...flt,
                values: cleanFilterValues.map((item) => {
                  return {
                    value: item,
                    text: filterValueToString(flt.key, item),
                  };
                }) as FilterValue[],
              }
            : { ...flt, value: filterValues as string };
        });
      }
      return [...prev];
    });

    if (filterDebounceTimer.current !== null) {
      clearTimeout(filterDebounceTimer.current);
    }

    filterDebounceTimer.current = setTimeout(() => {
      setPagination((prev) => {
        return {
          page: 1,
          pageSize: prev.pageSize,
        };
      });

      setUpdateCount((prev) => prev + 1);

      setFilterUpdateCount((prev) => prev + 1);

      filterDebounceTimer.current = null;
    }, 150);
  };

  const handleTableChange = (
    paginationUpdate: any,
    filtersUpdate: any,
    sorterUpdate: any,
    extra: any,
  ) => {
    logger.debug(extra.action);
    if (isNotNull(extra) && isNotNull(extra.action)) {
      switch (extra.action) {
        case 'paginate': {
          try {
            logger.debug(`Paging: ${JSON.stringify(paginationUpdate)}`);

            handlePagination(
              paginationUpdate.current,
              paginationUpdate.pageSize,
            );
          } catch {
            /* empty */
          }
          break;
        }
        case 'sort': {
          try {
            logger.debug(
              `Sort: column=${sorterUpdate?.columnKey ?? sorterUpdate?.field}, order=${sorterUpdate?.order}`,
            );

            const sortField = isNotNull(sorterUpdate?.columnKey)
              ? sorterUpdate.columnKey
              : sorterUpdate?.field;

            if (isNotNull(sortField) && sortField !== '') {
              handleSort(sortField, sorterUpdate.order);
            } else if (isNotNull(defaultSort)) {
              handleSort(defaultSort.field, defaultSort.direction);
            }
          } catch (ex) {
            logger.error(`sort ERROR: ${ex}`);
          }
          break;
        }
        case 'filter': {
          logger.debug(`Filter: ${JSON.stringify(filtersUpdate)}`);

          if (isNotNull(filtersUpdate)) {
            Object.keys(filtersUpdate).forEach((key) => {
              handleFilter(key, (filtersUpdate[key] as string[]) ?? []);
            });
          }
          break;
        }
        default: {
          logger.warn(`Unhandled action: ${extra.action}`);
        }
      }
    }
  };

  const mergeStaticFilters = (baseFilters: Filter[]): Filter[] => {
    const effectiveStaticFilters =
      isNotNull(staticFilters) && staticFilters.length > 0
        ? staticFilters
        : fixedStaticFilters;

    if (isNull(effectiveStaticFilters) || effectiveStaticFilters.length === 0) {
      return baseFilters;
    }

    const merged = [...baseFilters];

    effectiveStaticFilters.forEach((staticFilter) => {
      const filterFieldDef = filters.find(
        (flt) => flt.key === staticFilter.field,
      );
      const resolvedField =
        isNotNull(filterFieldDef) && filterFieldDef.filterField !== ''
          ? filterFieldDef.filterField
          : staticFilter.field;

      // A static filter with its own operator (e.g. nin) can't be merged into
      // a user's "in" filter on the same field; send it as a separate
      // condition, which the server ANDs with the rest.
      const hasOperator =
        isNotNull(staticFilter.operator) && staticFilter.operator !== 'in';
      const existingIndex = hasOperator
        ? -1
        : merged.findIndex(
            (flt) =>
              flt.field === resolvedField &&
              (isNull(flt.operator) || flt.operator === 'in'),
          );
      if (existingIndex === -1) {
        merged.push({ ...staticFilter, field: resolvedField });
      } else {
        merged[existingIndex] = {
          ...merged[existingIndex],
          values: [
            ...new Set([
              ...merged[existingIndex].values,
              ...staticFilter.values,
            ]),
          ],
        };
      }
    });

    return merged;
  };

  const buildFilters = () => {
    const paramFilters: Filter[] = [];

    if (isNotNull(filters)) {
      filters.forEach((filter) => {
        if (isNotNull(filter)) {
          if (
            filter.multiple &&
            filter.filterField !== '' &&
            filter.values.length > 0
          ) {
            const newFilter: Filter = {
              field: filter.filterField,
              values: filter.values
                .filter((item) => isNotNull(item.value) && item.value !== '')
                .map((item) => item.value),
            };

            if (isNotNull(filter.operator) && filter.operator !== '') {
              newFilter.operator = filter.operator;
            }
            paramFilters.push(newFilter);
          } else if (
            !filter.multiple &&
            filter.filterField !== '' &&
            isNotNull(filter.value) &&
            filter.value !== ''
          ) {
            const newFilter: Filter = {
              field: filter.filterField,
              values: [filter.value],
            };

            if (isNotNull(filter.operator) && filter.operator !== '') {
              newFilter.operator = filter.operator;
            }

            paramFilters.push(newFilter);
          }
        }
      });
    }

    return paramFilters;
  };

  const getSortDirection = () => {
    const direction =
      isNotNull(sort) && sort.direction !== ''
        ? sort.direction
        : isNotNull(defaultSort)
          ? defaultSort.direction
          : 'ascend';

    if (direction === 'descend') {
      return -1;
    }

    return 1;
  };

  const buildParameters = () => {
    const baseFilters = buildFilters();

    const sortColumn = filters.find((chk) => chk.key === sort.field);
    let sortField = sortColumn?.sortField || sort.field;
    if (isNull(sortField) || sortField === '') {
      sortField = isNotNull(defaultSort) ? defaultSort.field : '';
    }

    const params: Params = {
      paging: {
        page: pagination.page,
        pageSize: pagination.pageSize,
      },
      sort: {
        field: sortField,
        direction: sortColumn?.invertSort
          ? -getSortDirection()
          : getSortDirection(),
      },
      filters: mergeStaticFilters(baseFilters),
    };
    return params;
  };

  const setFilterOptions = (key: string, options: FilterOption[]): void => {
    setFilters((prev) =>
      prev.map((flt) => (flt.key === key ? { ...flt, options } : flt)),
    );
  };

  const getFilterField = (key: string): FilterField | null => {
    const filter = filters.find((flt) => flt.key === key);
    return isNotNull(filter) ? filter : null;
  };

  const getFilterOptions = (key: string): FilterOption[] => {
    const filter = filters.find((flt) => flt.key === key);

    return filter?.options ?? EMPTY_OPTIONS;
  };

  const getFilterValues = (key: string): string[] => {
    const filter = filters.find((flt) => flt.key === key);

    return filter?.values.map((item) => item.value) ?? [];
  };

  const getFilterValue = (key: string): string => {
    const filter = filters.find((flt) => flt.key === key);

    return filter?.value ?? '';
  };

  // Stable reference: only changes when pagination, sort, or filters actually change.
  // Without this, the inline object literal creates a new reference on every render,
  // causing any useEffect that depends on `input` to fire on every parent re-render.
  const input = useMemo(
    () => ({ pagination, sort, filters }),
    [pagination, sort, filters],
  );

  return (
    <InputContext.Provider
      value={{
        input,
        staticFilters,
        handleSort,
        handlePagination,
        handleFilter,
        handleTableChange,
        buildParameters,
        setStaticFilters,
        setFilterOptions,
        getFilterField,
        getFilterOptions,
        getFilterValues,
        getFilterValue,
        updateCount,
        filterUpdateCount,
        pagingUpdateCount,
        sortingUpdateCount,
        isReady,
      }}
    >
      {isNotNull(useUrlParams) && useUrlParams && <InputQueryInterface />}
      {children}
    </InputContext.Provider>
  );
};

export default InputContainer;
